import React, { useRef } from 'react';
import GradeDistributionChart from './charts/GradeDistributionChart.js';
import RoutesPerYearChart from './charts/RoutesPerYearChart.js';
import AscentProgressionChart from './charts/AscentProgressionChart.js';
import CragStatsChart from './charts/CragStatsChart.js';
import { formatGrade } from '@utils/gradeUtils.js';

const PosterLayout = ({
  selectedCharts,
  filteredData,
  climbingStyle,
  gradeSystem,
  onGradeClick,
  chartLayouts,
  lockedMode = null,
  onExitPosterMode,
  climberName,
  selectedYear
}) => {
  const posterRef = useRef(null);

  const defaultSettings = {
    showDistinctRoutes: true,
    selectedStat: 'cleanRoutes',
    numCrags: 30,
  };

  const chartTitles = {
    grades: 'Grade Distribution',
    flash: 'Flash Probability',
    progression: 'Grade Progression',
    ascents: 'Ascent History',
    routesPerYear: 'Cumulative Routes',
    crags: 'Top Crags',
    types: 'Ascent Types',
    projects: 'Projects',
    cragPerformance: 'Crag Performance'
  };

  const getBaseFilename = () => {
    if (!filteredData?.length || !filteredData[0]?.filename) {
      return 'climbing-stats';
    }
    const filename = filteredData[0].filename;
    return filename
      .replace(/\.csv$/i, '')
      .replace(/[^\w\-\s]/g, '')
      .replace(/\s+/g, '_');
  };

  const handleSaveAsPNG = async () => {
    try {
      const html2canvas = (await import('html2canvas')).default;
      
      if (!posterRef.current) return;

      const canvas = await html2canvas(posterRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
      });

      const baseFilename = getBaseFilename();
      const link = document.createElement('a');
      link.download = `${baseFilename}_poster.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Failed to export PNG:', error);
    }
  };

  const handleSaveAsSVG = () => {
    try {
      if (!posterRef.current) return;

      // Create new SVG container
      const containerSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      const width = posterRef.current.clientWidth;
      const height = posterRef.current.clientHeight;
      containerSvg.setAttribute('width', width);
      containerSvg.setAttribute('height', height);
      containerSvg.setAttribute('viewBox', `0 0 ${width} ${height}`);

      // Add white background
      const background = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      background.setAttribute('width', '100%');
      background.setAttribute('height', '100%');
      background.setAttribute('fill', 'white');
      containerSvg.appendChild(background);

      // Add grid lines
      const gridGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      gridGroup.setAttribute('stroke', '#e5e7eb'); // border-gray-200 equivalent
      gridGroup.setAttribute('stroke-width', '1');
      // Add vertical and horizontal lines for grid
      containerSvg.appendChild(gridGroup);

      // Function to create text element
      const createText = (x, y, text, fontSize = '18', fontWeight = '600') => {
        const textElement = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        textElement.setAttribute('x', x);
        textElement.setAttribute('y', y);
        textElement.setAttribute('font-size', fontSize);
        textElement.setAttribute('font-family', 'sans-serif');
        textElement.setAttribute('font-weight', fontWeight);
        textElement.textContent = text;
        return textElement;
      };

      // Process each chart section
      selectedCharts.forEach((metric, index) => {
        const chartContainer = posterRef.current.querySelectorAll('.chart-container')[index];
        if (!chartContainer) return;

        const chartRect = chartContainer.getBoundingClientRect();
        const posterRect = posterRef.current.getBoundingClientRect();
        
        // Calculate positions
        const col = index % 2;
        const row = Math.floor(index / 2);
        const x = chartRect.left - posterRect.left;
        const y = chartRect.top - posterRect.top;
        
        // Create group for this chart section
        const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        g.setAttribute('transform', `translate(${x}, ${y})`);

        // Add title
        const titleText = createText(20, 30, chartTitles[metric]);
        g.appendChild(titleText);

        // Get and clone the chart SVG
        const chartSvg = chartContainer.querySelector('svg');
        if (chartSvg) {
          const chartClone = chartSvg.cloneNode(true);
          chartClone.setAttribute('y', '48'); // Below title
          g.appendChild(chartClone);
        }

        containerSvg.appendChild(g);
      });

      // Download the SVG
      const svgData = new XMLSerializer().serializeToString(containerSvg);
      const blob = new Blob([svgData], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      
      const baseFilename = getBaseFilename();
      const link = document.createElement('a');
      link.download = `${baseFilename}_poster.svg`;
      link.href = url;
      link.click();
      
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export SVG:', error);
    }
  };

  const renderChart = (metric) => {
    switch (metric) {
      case 'crags':
        return (
          <CragStatsChart
            data={filteredData}
            climbingStyle={climbingStyle}
            selectedStat={defaultSettings.selectedStat}
            numCrags={defaultSettings.numCrags}
          />
        );
      case 'grades':
        return (
          <GradeDistributionChart 
            data={filteredData}
            climbingStyle={climbingStyle}
            gradeSystem={gradeSystem}
            formatGradeWithStyle={formatGrade}
            onGradeClick={onGradeClick}
            isHorizontal={chartLayouts[metric]}
            showDistinctRoutes={defaultSettings.showDistinctRoutes}
          />
        );
      case 'routesPerYear':
        return (
          <RoutesPerYearChart
            data={filteredData}
            climbingStyle={climbingStyle}
            selectedStat={defaultSettings.selectedStat}
          />
        );
      case 'ascents':
        return (
          <AscentProgressionChart
            data={filteredData}
            getGradeLabel={formatGrade}
            climbingStyle={climbingStyle}
            gradeSystem={gradeSystem}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="relative">
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
        <button
          onClick={handleSaveAsPNG}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm"
        >
          Save as PNG
        </button>
        <button
          onClick={handleSaveAsSVG}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm"
        >
          Save as SVG
        </button>
        {!lockedMode && (
          <button
            onClick={onExitPosterMode}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm"
          >
            Exit Poster Mode
          </button>
        )}
      </div>
      
      <div ref={posterRef} className="bg-white p-8">
        {/* Poster Header */}
        <div className="mb-4 pb-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold">
              {climberName ? `${climberName} Climbing Dashboard` : 'Climbing Dashboard'}
            </h1>
            <div className="flex items-center gap-4 text-gray-600">
              <span>{filteredData?.length?.toLocaleString() || 0} ascents</span>
              <span className="capitalize">{climbingStyle}</span>
              <span>{selectedYear === 'all' ? 'All Time' : selectedYear}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 border border-gray-200">
          {selectedCharts.map((metric, index) => (
            <div 
              key={index} 
              className={`
                flex flex-col
                ${index % 2 === 0 ? 'border-r border-gray-200' : ''} 
                ${index < 2 ? 'border-b border-gray-200' : ''}
              `}
            >
              <div className="h-12 px-4 flex items-center bg-gray-50 border-b border-gray-200">
                <h2 className="text-lg font-semibold">{chartTitles[metric]}</h2>
              </div>
              <div className="h-[460px] p-4 chart-container">
                {renderChart(metric)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PosterLayout;